/**
 * CardZoomViewer — the enlarged view of one printed Priority Values card.
 *
 * WHY A SEPARATE VIEWER
 * ---------------------
 * The lightbox shows the card at `min(78vw, 22rem)` — big enough to read
 * the value's name, far too small to read the printed detail (the slogan
 * line, the fine print at the foot of the card). The artwork is 1024px
 * square, so there are pixels to spare: the card can be shown at 1:1 and
 * actually be read.
 *
 * THE ZOOM MODEL
 * --------------
 * Zoom is expressed as a PERCENTAGE OF THE NATIVE ARTWORK, not as a
 * multiplier of the on-screen size. That makes the numbers meaningful:
 * 100% means one artwork pixel per device-independent pixel, which is the
 * sharpest the file can be shown. The scale therefore has a floor (the
 * whole card must stay inside the viewport) and a ceiling (300% — past
 * that the file is just being smeared, and pretending otherwise would be
 * a lie about the print).
 *
 * Panning is clamped to the artwork's own bounds: the image can never be
 * dragged off-screen, so there is always something to drag back.
 *
 * INPUTS
 * ------
 *   • drag — pans (pointer events, so one code path for mouse and touch)
 *   • wheel — zooms about the centre
 *   • pinch — zooms about the midpoint of the two fingers
 *   • double-click / double-tap — toggles fit ⇄ 1:1
 *   • + / − / 0 — zoom in, out, reset to 1:1
 *   • arrows — pan
 *   • Escape — leaves the zoom view (handled by the parent dialog, which
 *     asks this viewer to step aside before it closes)
 *
 * The wheel listener is attached natively with `passive: false`, because
 * React attaches its synthetic wheel handler at the root as a PASSIVE
 * listener — `preventDefault()` inside `onWheel` does nothing there, and
 * the page would scroll behind the overlay instead of zooming.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Icon } from '@iconify/react'

/** The artwork's native size, in pixels. Every card is 1024×1024. */
const NATIVE = 1024

/** The furthest the viewer will zoom. Past this the file is only smeared. */
const MAX_PCT = 300

/** How much of the viewport the fitted card fills. */
const FIT_RATIO = 0.94

/** A zoom step, in percentage points. */
const STEP = 25

interface CardZoomViewerProps {
  /** The card artwork. */
  src: string
  /** The card's name, for the accessible label of the image. */
  name: string
  /** Leave the zoom view and go back to the card. */
  onExit: () => void
}

export default function CardZoomViewer({ src, name, onExit }: CardZoomViewerProps) {
  const { t } = useTranslation()
  const stageRef = useRef<HTMLDivElement>(null)
  const rootRef = useRef<HTMLDivElement>(null)

  /** The viewport, measured so the fit and the pan bounds are exact. */
  const [box, setBox] = useState({ w: 0, h: 0 })
  /** Displayed width as a percentage of the 1024px artwork. */
  const [pct, setPct] = useState(100)
  /** The pan offset, in CSS pixels, from the centre of the artwork. */
  const [offset, setOffset] = useState({ x: 0, y: 0 })

  /**
   * Mirrors of the two live values.
   *
   * Pointer handlers need the *current* zoom and pan, but a React state
   * captured in a closure is one render old — which is exactly long
   * enough for a drag to overshoot its clamp. These refs are written
   * at the same time as the state, so the handlers always read the truth.
   */
  const pctRef = useRef(pct)
  const offsetRef = useRef(offset)

  const applyPct = useCallback((next: number) => {
    pctRef.current = next
    setPct(next)
  }, [])

  const applyOffset = useCallback((next: { x: number; y: number }) => {
    offsetRef.current = next
    setOffset(next)
  }, [])

  /* ── Measure the stage ─────────────────────────────────────── */
  useEffect(() => {
    const el = stageRef.current
    if (!el) return
    const measure = () => {
      const r = el.getBoundingClientRect()
      setBox({ w: r.width, h: r.height })
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  /**
   * The zoom at which the whole card fits in the viewport.
   *
   * The artwork is square, so the fitting size is the smaller of the
   * viewport's two sides.
   */
  const fitPct = box.w ? (Math.min(box.w, box.h) * FIT_RATIO) / NATIVE * 100 : 100

  /** The artwork's on-screen size, in CSS pixels. */
  const shown = (NATIVE * pct) / 100

  /**
   * The pan bounds for a given zoom level: how far the artwork may
   * travel before its edge reaches the middle of the viewport.
   */
  const boundsAt = useCallback(
    (level: number) => {
      const size = (NATIVE * level) / 100
      return {
        x: Math.max(0, (size - box.w) / 2),
        y: Math.max(0, (size - box.h) / 2),
      }
    },
    [box.w, box.h],
  )

  /**
   * Set the zoom, and re-centre the pan.
   *
   * Re-centring is the right call at every step: the user asked to see
   * more of the card, and the middle of the card is the part they were
   * already looking at. Keeping the pan would leave them staring at the
   * edge they had dragged to.
   */
  const setZoom = useCallback(
    (next: number) => {
      applyPct(Math.min(MAX_PCT, Math.max(1, next)))
      applyOffset({ x: 0, y: 0 })
    },
    [applyPct, applyOffset],
  )

  /**
   * Start at the fit, so the whole card is visible on arrival.
   *
   * The guard is `box.w > 0`, not `fitPct > 0`: before the stage has
   * been measured `fitPct` falls back to 100, so guarding on the
   * percentage would latch the initial 100% and never fit the card.
   * The `initialised` ref keeps this to the first real measurement —
   * re-fitting on every viewport change would throw away a zoom the
   * user had set.
   */
  const initialised = useRef(false)
  useEffect(() => {
    if (initialised.current || box.w === 0) return
    initialised.current = true
    applyPct(fitPct)
  }, [box.w, fitPct, applyPct])

  /**
   * Take the keyboard.
   *
   * The viewer is a focusable region of its own: without this, the arrow
   * keys would still reach the lightbox behind it and flip to the
   * previous card instead of panning the artwork.
   */
  useEffect(() => {
    rootRef.current?.focus()
  }, [])

  /* ── Wheel zoom (native, non-passive) ──────────────────────── */
  useEffect(() => {
    const el = stageRef.current
    if (!el) return
    const onWheel = (event: WheelEvent) => {
      event.preventDefault()
      const delta = event.deltaY < 0 ? STEP : -STEP
      applyPct(Math.min(MAX_PCT, Math.max(fitPct, pctRef.current + delta)))
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [fitPct, applyPct])

  /* ── Drag to pan, pinch to zoom ────────────────────────────── */
  /** Every pointer currently on the stage, by pointer id. */
  const pointers = useRef(new Map<number, { x: number; y: number }>())
  /** The pinch baseline: the distance and zoom the gesture started from. */
  const pinch = useRef<{
    dist: number
    pct: number
    mid: { x: number; y: number }
  } | null>(null)
  /** The drag baseline: where the pointer went down, and the pan then. */
  const drag = useRef<{ id: number; x: number; y: number; ox: number; oy: number } | null>(
    null,
  )

  const onPointerDown = (event: React.PointerEvent) => {
    if (event.button !== undefined && event.button !== 0) return
    ;(event.currentTarget as HTMLElement).setPointerCapture(event.pointerId)
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY })
    if (pointers.current.size >= 2) {
      // Two fingers: the gesture is a pinch, not a drag. Re-baseline it.
      const [a, b] = [...pointers.current.values()]
      pinch.current = {
        dist: Math.hypot(a.x - b.x, a.y - b.y) || 1,
        pct: pctRef.current,
        mid: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
      }
      drag.current = null
      return
    }
    drag.current = {
      id: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      ox: offsetRef.current.x,
      oy: offsetRef.current.y,
    }
  }

  const onPointerMove = (event: React.PointerEvent) => {
    const point = pointers.current.get(event.pointerId)
    if (!point) return
    point.x = event.clientX
    point.y = event.clientY

    const values = [...pointers.current.values()]
    if (values.length >= 2 && pinch.current) {
      /* ── Pinch ──────────────────────────────────────────────
         The zoom follows the ratio of the finger distance, and the
         artwork follows the midpoint, so the point under the fingers
         stays under the fingers. */
      const [a, b] = values
      const dist = Math.hypot(a.x - b.x, a.y - b.y) || 1
      const ratio = dist / pinch.current.dist
      const nextPct = Math.min(
        MAX_PCT,
        Math.max(fitPct, pinch.current.pct * ratio),
      )
      const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }
      const bounds = boundsAt(nextPct)
      const cur = offsetRef.current
      applyPct(nextPct)
      applyOffset({
        x: Math.min(bounds.x, Math.max(-bounds.x, cur.x + (mid.x - pinch.current.mid.x))),
        y: Math.min(bounds.y, Math.max(-bounds.y, cur.y + (mid.y - pinch.current.mid.y))),
      })
      // Re-baseline, so the next move measures from here.
      pinch.current = { dist, pct: nextPct, mid }
      return
    }

    /* ── Single-pointer drag ───────────────────────────────── */
    const base = drag.current
    if (!base || base.id !== event.pointerId) return
    const bounds = boundsAt(pctRef.current)
    applyOffset({
      x: Math.min(bounds.x, Math.max(-bounds.x, base.ox + (event.clientX - base.x))),
      y: Math.min(bounds.y, Math.max(-bounds.y, base.oy + (event.clientY - base.y))),
    })
  }

  /** Release a pointer: hand a one-finger drag over, or end the pinch. */
  const onPointerUp = (event: React.PointerEvent) => {
    pointers.current.delete(event.pointerId)
    try {
      ;(event.currentTarget as HTMLElement).releasePointerCapture(event.pointerId)
    } catch {
      /* the pointer was already released — nothing to undo */
    }
    if (pointers.current.size < 2) pinch.current = null
    if (pointers.current.size === 1) {
      // One finger left: hand the drag over to it, without a jump.
      const [id, point] = [...pointers.current.entries()][0]
      drag.current = {
        id,
        x: point.x,
        y: point.y,
        ox: offsetRef.current.x,
        oy: offsetRef.current.y,
      }
    } else {
      drag.current = null
    }
  }

  /* ── Keyboard ──────────────────────────────────────────────── */
  const onKeyDown = (event: React.KeyboardEvent) => {
    // The lightbox behind this viewer listens for the arrow keys to flip
    // between cards. Stop the event here so the arrows pan the artwork
    // instead of changing the card behind it.
    event.stopPropagation()
    const nudge = event.shiftKey ? 80 : 30
    const bounds = boundsAt(pctRef.current)
    const cur = offsetRef.current
    switch (event.key) {
      case '+':
      case '=':
        event.preventDefault()
        applyPct(Math.min(MAX_PCT, pctRef.current + STEP))
        break
      case '-':
      case '_':
        event.preventDefault()
        applyPct(Math.max(fitPct, pctRef.current - STEP))
        break
      case '0':
        event.preventDefault()
        setZoom(100)
        break
      case 'ArrowLeft':
        event.preventDefault()
        applyOffset({ ...cur, x: Math.max(-bounds.x, cur.x + nudge) })
        break
      case 'ArrowRight':
        event.preventDefault()
        applyOffset({ ...cur, x: Math.min(bounds.x, cur.x - nudge) })
        break
      case 'ArrowUp':
        event.preventDefault()
        applyOffset({ ...cur, y: Math.max(-bounds.y, cur.y + nudge) })
        break
      case 'ArrowDown':
        event.preventDefault()
        applyOffset({ ...cur, y: Math.min(bounds.y, cur.y - nudge) })
        break
      default:
        break
    }
  }

  /* ── Double-click / double-tap: fit ⇄ 1:1 ──────────────────── */
  const onDoubleClick = () => {
    if (pct > fitPct + 1) setZoom(fitPct)
    else setZoom(100)
  }

  const pctLabel = Math.round(pct)

  return (
    <div
      ref={rootRef}
      className="pv-zoom"
      onKeyDown={onKeyDown}
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      aria-label={name}
    >
      <div
        ref={stageRef}
        className="pv-zoom__stage"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onDoubleClick={onDoubleClick}
      >
        <img
          className="pv-zoom__img"
          src={src}
          alt={name}
          width={NATIVE}
          height={NATIVE}
          draggable={false}
          style={{
            width: `${shown}px`,
            height: `${shown}px`,
            transform: `translate3d(${offset.x}px, ${offset.y}px, 0)`,
          }}
        />
      </div>

      {/* The toolbar. Kept out of the stage so a drag on it never pans. */}
      <div className="pv-zoom__bar">
        <button
          type="button"
          className="pv-zoom__btn"
          onClick={() => applyPct(Math.max(fitPct, pctRef.current - STEP))}
          aria-label={t('products.offers.valuesIncubator.priorityCards.zoomOut')}
        >
          <Icon icon="lucide:minus" />
        </button>
        <span className="pv-zoom__level" aria-live="polite">
          {pctLabel}%
        </span>
        <button
          type="button"
          className="pv-zoom__btn"
          onClick={() => applyPct(Math.min(MAX_PCT, pctRef.current + STEP))}
          aria-label={t('products.offers.valuesIncubator.priorityCards.zoomIn')}
        >
          <Icon icon="lucide:plus" />
        </button>
        <button
          type="button"
          className="pv-zoom__btn pv-zoom__btn--wide"
          onClick={onDoubleClick}
          aria-label={t('products.offers.valuesIncubator.priorityCards.zoomReset')}
        >
          <Icon icon="lucide:scan" />
          <span className="hidden sm:inline">
            {t('products.offers.valuesIncubator.priorityCards.zoomReset')}
          </span>
        </button>
      </div>

      <p className="pv-zoom__hint">
        {t('products.offers.valuesIncubator.priorityCards.zoomHint')}
      </p>

      <button
        type="button"
        className="pv-zoom__close"
        onClick={onExit}
        aria-label={t('products.offers.valuesIncubator.priorityCards.zoomExit')}
      >
        <Icon icon="lucide:x" />
      </button>
    </div>
  )
}
